// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { solveUnpaidBreaksStageWithTargeting } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { z } from "zod";

import { inAppRatingConfigurationSchema } from "../App/useInAppRatingPrompt/types";
import { DayOfTheWeek } from "../lib/dates/types";
import { CbhFeatureFlag } from "./CbhFeatureFlag";

export enum DocumentVerificationProcessVariants {
  PLACEHOLDER = "placeholder",
  INTERNAL = "INTERNAL",
  STRIPE_IDENTIFY_SDK = "STRIPE_IDENTIFY_SDK",
}

export enum SignupJobSearchAgreementState {
  HIDDEN = "HIDDEN",
  OPTIONAL = "OPTIONAL",
  REQUIRED = "REQUIRED",
}

export enum OpenShiftCalendarV2CellVariant {
  OPEN_SHIFT_V0_CELL = "OpenShiftV0Cell",
  OPEN_SHIFT_HEAT_MAP_CELL = "OpenShiftHeatMapCell",
}

export const PenaltyMultiplierSchema = z.object({
  minInfractions: z.number(),
  multiplier: z.number(),
});

export const PenaltyConfigSchema = z.object({
  multipliers: z.array(PenaltyMultiplierSchema),
  maximumShiftsToCheck: z.number(),
});

export const JobFairPolicyConfigSchema = z.object({
  policyId: z.number().optional(),
});

export type PenaltyMultiplier = z.infer<typeof PenaltyMultiplierSchema>;

const limitedBasketSizeConfigurationSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(false),
  }),
  z.object({
    enabled: z.literal(true),
    scoreThreshold: z.number().default(150),
    basketSize: z.number().default(1),
    leadTimeThresholdInHrs: z.number().default(120),
    shiftRequirements: z.array(z.string()).default(["CNA", "LVN", "RN", "NURSE", "CAREGIVER"]),
  }),
]);

export type LimitedBasketSizeConfiguration = z.infer<typeof limitedBasketSizeConfigurationSchema>;

const warningBasketSizeConfigurationSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(false),
  }),
  z.object({
    enabled: z.literal(true),
    scoreThreshold: z.number().default(100),
    basketSize: z.number().default(1),
    leadTimeThresholdInHrs: z.number().default(120),
    failRestrictionDurationInDays: z.number().default(180),
    shiftRequirements: z.array(z.string()).default(["CNA", "LVN", "RN", "NURSE", "CAREGIVER"]),
  }),
]);

export type WarningBasketSizeConfiguration = z.infer<typeof warningBasketSizeConfigurationSchema>;

const inAppAdActionSchema = z.object({
  id: z.string(),
  copy: z.string(),
  logName: z.string(),
  link: z.string(),
  linkType: z.enum(["in-app", "external-in-app-browser", "external-out-of-app-browser"]),
  variant: z.enum(["contained", "outlined"]).optional(),
  color: z.string().optional(),
  size: z.string().optional(),
});

export const InAppAdAttributesSchema = z.object({
  appId: z.string(),
  image: z.object({
    url: z.string(),
    width: z.number(),
    height: z.number(),
    parentPadding: z.number().optional(),
  }),
  type: z.literal("in-app-ad"),
  title: z.string(),
  incentivizeCopy: z.string().optional(),
  incentivizeAmount: z.string().optional(),
  pillTexts: z.array(
    z.object({
      text: z.string(),
      intent: z.string().optional(),
    })
  ),
  shortDescription: z.string(),
  longDescriptionHeadline: z.string(),
  longDescriptionText: z.string(),
  start: z.string().optional(),
  actions: z.object({
    primary: inAppAdActionSchema.optional(),
    secondary: inAppAdActionSchema.optional(),
    tertiary: inAppAdActionSchema.optional(),
    quaternary: inAppAdActionSchema.optional(),
  }),
});

export const InAppAdInjectorOptionsSchema = z.object({
  displayOnEmptyState: z.boolean(),
  campaignStart: z.string(), // ISO date string
  campaignEnd: z.string(), // ISO date string
  maxAllowedOccurrences: z.number(),
  interval: z.number(),
  maxAllowedViews: z.number(),
});

const activeLicenseStatusRuleSchema = z.object({
  qualification: z.array(z.string()),
  state: z.array(z.string()).optional(),
});

const licenseDefaultStatusSchema = z.object({
  activeStatusRules: z.array(activeLicenseStatusRuleSchema),
});

export type ActiveLicenseStatusRule = z.infer<typeof activeLicenseStatusRuleSchema>;
export type LicenseDefaultStatusConfig = z.infer<typeof licenseDefaultStatusSchema>;

const hcpLicenseFieldRequirementSchema = z
  .union([z.literal("visible"), z.literal("hidden")])
  .optional();

export const DynamicPenaltiesConfigSchema = z.discriminatedUnion("enabled", [
  // when disabled everything else is expected to be undefined
  z.object({
    enabled: z.literal(false),
  }),

  // when enabled everything else should be defined
  z.object({
    enabled: z.literal(true),
    latenessPenalty: PenaltyConfigSchema,
    cancellationPenalty: PenaltyConfigSchema,
    policyId: z.number(),
    reEnrollmentScore: z.number(),
    earliestDateToCheck: z.string(),
    maximumLeadTimeToCheck: z.number(),
  }),
]);

export type DynamicPenaltiesConfig = z.infer<typeof DynamicPenaltiesConfigSchema>;

export const timelinessBonusRedesignSchema = z.discriminatedUnion("enabled", [
  z.object({ enabled: z.literal(false) }),
  z.object({
    enabled: z.literal(true),
    sendNotificationForDelayedBonus: z.boolean(),
    goLiveDate: z.string().optional(),
  }),
]);

export type TimelinessBonusRedesignConfig = z.infer<typeof timelinessBonusRedesignSchema>;

const qualificationCategoriesConfigSchema = z.record(z.array(z.string()));

export type QualificationCategoriesConfig = z.infer<typeof qualificationCategoriesConfigSchema>;

const hiringIncentiveProgramSchema = z.union([
  z.object({ enabled: z.literal(false) }),
  z.object({
    enabled: z.literal(true),
    type: z.literal("SIGN_ON_BONUS"),
    amountInMinorUnits: z.number(),
  }),
  z.object({
    enabled: z.literal(true),
    type: z.literal("CARE_PACKAGE"),
  }),
]);
export type HiringIncentiveProgram = z.infer<typeof hiringIncentiveProgramSchema>;

const emergencyCreditsConfigCreditSchema = z.object({
  creditCap: z.number().min(1),
  creditsToStartWith: z.number().min(0),
  requiredShiftsToEarnCredit: z.number().min(1),
});
const emergencyCreditsConfigSchema = z.discriminatedUnion("enabled", [
  z.object({ enabled: z.literal(false) }),
  z.object({
    enabled: z.literal(true),
    cancellation: emergencyCreditsConfigCreditSchema,
    lateness: emergencyCreditsConfigCreditSchema,
  }),
]);
export type EmergencyCreditsConfig = z.infer<typeof emergencyCreditsConfigSchema>;

export const cbhFeatureFlags = {
  [CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC]: z.boolean(),
  [CbhFeatureFlag.ENABLE_COGNITO_ONBOARDING]: z.boolean(),
  [CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS]: z.boolean(),
  [CbhFeatureFlag.ENABLE_COGNITO_PHONE_LOGIN]: z.boolean(),
  [CbhFeatureFlag.RUN_BACKGROUND_CHECK_ON_BOOKING]: z.object({
    isEnabled: z.boolean(),
    checkrSdkUrl: z.string(),
    autoShiftUnassignmentValidityWindowInDays: z.number().optional(),
    isDelayedBackgroundCheckNoticeEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.PREFERRED_PERSISTENCE_FOR_AUTH]: z.object({
    preferredPersistence: z.enum(["localstorage", "indexeddb"]).optional(),
  }),
  [CbhFeatureFlag.LICENSE_RECIPROCITY]: z.boolean(),
  [CbhFeatureFlag.WHITE_SCREEN_DETECTION]: z.object({
    isEnabled: z.boolean(),
    checkIntervalMs: z.number().int().positive(),
    logIntervalMs: z.number().int().positive(),
    maximumNumberOfElementsForWhiteScreen: z.number().int().positive(),
  }),
  [CbhFeatureFlag.ATTENDANCE_SCORE_POLICY_LINKS]: z
    .object({
      doNotReturnPolicy: z.object({ label: z.string(), url: z.string() }),
      howAttendanceWorks: z.object({ label: z.string(), url: z.string() }),

      // deprecated and will be removed in the future
      suspensionDeactivationPolicy: z.object({ label: z.string(), url: z.string() }).optional(),
      understandingAccountStatus: z.object({ label: z.string(), url: z.string() }).optional(),
    })
    .partial(),
  [CbhFeatureFlag.AUTO_VERIFICATION_DOCUMENT_CLEANUP_ENABLED]: z.boolean(),
  [CbhFeatureFlag.SENT_HOME_REQUEST_CONFIG]: z.object({
    failureToast: z.object({
      header: z.string(),
      message: z.string(),
    }),
    successToast: z.object({
      header: z.string(),
      message: z.string(),
    }),
    confirmationAlert: z.object({
      header: z.string(),
      message: z.string(),
    }),
    isSentHomeFlowV2Enabled: z.boolean(),
  }),
  [CbhFeatureFlag.CAMERA_IMAGE_QUALITY]: z.number(),
  [CbhFeatureFlag.SIGNUP_REFERRAL_BONUS_PAYMENT_ETA_IN_DAYS]: z.number(),
  [CbhFeatureFlag.ENABLED_SIGNUP_REFERRAL_GUARD_RAILS]: z
    .object({
      enableCoreRequirementsVerification: z.boolean(),
      enableLocationVerification: z.boolean(),
      enableManualLicenseVerification: z.boolean(),
    })
    .partial(),
  [CbhFeatureFlag.SUPPORT_LINKS]: z.object({
    HOW_TO_CONTACT_SUPPORT: z.string(),
    WHY_SSN: z.string(),
    HELP_CENTER_WORKER: z.string(),
    BACKGROUND_CHECKS: z.string(),
    TIMESHEET_SIGNATURES: z.string(),
    ATTENDANCE_SCORE: z.string(),
    DNR_REQUESTS_WORKER: z.string(),
    MULTI_STATE_LICENSES: z.string(),
    WORK_WITH_FRIENDS: z.string(),
    RATE_NEGOTIATION_WORKER: z.string(),
    REFERRAL_BONUS: z.string(),
    SUSPENSION_AND_DEACTIVATION_RULES: z.string(),
    INSTANT_PAY_INFO: z.string(),
    EXTRA_WORK_BREAKS: z.string(),
    EDITING_CLOCK_IN_AND_OUT_TIMES: z.string(),
    CLOCKING_IN_WITH_NFC_TAGS: z.string(),
    DYNAMIC_PENALTIES: z.string(),
    PRIORITY_ACCESS: z.string(),
    BREAK_PAYMENT_REQUESTS: z.string(),
    PAPER_TIMESHEET_INSTRUCTIONS: z.string(),
    SHIFT_BLOCKS: z.string(),
    SKILLS_ASSESSMENT_CONFIG: z.string(),
    REFERRAL_SHIFT_ATTENDANCE_RATE: z.string(),
    AFFILIATE_GUIDE: z.string(),
    REFERRAL_GUIDE: z.string(),
    REFERRAL_POLICY: z.string(),
    WHY_WORKERS_SEE_QUIZ: z.string(),
    CLIPBOARD_SCORE: z.string(),
    LIMITED_STATUS: z.string(),
    IN_HOME_FAQ: z.string(),
    IN_HOME_DEMO: z.string(),
    PLACEMENT_CLAIM_SIGN_ON_BONUS: z.string(),
    PLACEMENT_INTERVIEW_ATTENDANCE_POLICY: z.string(),
    PLACEMENT_INTERVIEW_ATTENDANCE_SIMPLIFIED_POLICY: z.string(),
    WORKER_REFERRAL_PROGRAM: z.string(),
    SIGN_IC_AGREEMENT: z.string(),
    RENEW_CNA_LICENSE: z.string(),
    INVITE_REQUIRED: z.string(),
  }),
  [CbhFeatureFlag.CAMERA_IMAGE_SIZE]: z.object({ height: z.number() }).partial(),
  [CbhFeatureFlag.CONVERT_TO_NONIP_AFTER_X_MINUTES]: z.number(),
  [CbhFeatureFlag.DOCUMENT_VERIFICATION_PROCESS]: z.nativeEnum(DocumentVerificationProcessVariants),
  [CbhFeatureFlag.ENABLE_CHECKR_PILL_STATUS]: z.boolean(),
  [CbhFeatureFlag.ENABLE_GEOLOCATION_TRACKING_FOR_SHIFTS]: z.record(z.string(), z.boolean()),
  [CbhFeatureFlag.ENABLE_LATE_FOR_SHIFT]: z.boolean(),
  [CbhFeatureFlag.ENABLE_PA_NOTIFICATION_PREFERENCE]: z.boolean(),
  [CbhFeatureFlag.ENABLE_REMAINING_SHIFT_DATA_IN_A_SLOT]: z.boolean(),
  [CbhFeatureFlag.ENABLE_TAG_LOCATION_POPUP]: z.boolean(),
  [CbhFeatureFlag.WORK_WITH_FRIENDS_BONUSES]: z
    .object({
      addFriendBonus: z.number(),
      groupAttendanceBonus: z.number(),
    })
    .partial(),
  [CbhFeatureFlag.URGENT_SHIFTS_HOURLY_PAY_RATE]: z.boolean(),
  [CbhFeatureFlag.URGENT_SHIFTS]: z.boolean(),
  [CbhFeatureFlag.USE_ORIGINALAMOUNT_FIELD_NON_IP]: z.boolean(),
  [CbhFeatureFlag.WORKER_TO_WORKPLACE_AFFILIATE_EXPERIMENT]: z.boolean(),
  [CbhFeatureFlag.MAX_DISTANCE_ALLOWED_FOR_LOCATION_VERIFICATION_IN_MILES]: z.number(),
  [CbhFeatureFlag.SIGNUP_BASED_REFERRAL]: z.record(
    z.string(),
    z.union([z.number(), z.record(z.string(), z.number())])
  ),
  [CbhFeatureFlag.HCP_LICENSE_DEFAULT_STATUS]: licenseDefaultStatusSchema,
  [CbhFeatureFlag.OPEN_SHIFT_CALENDAR_V2_CELL_VARIANTS]: z
    .nativeEnum(OpenShiftCalendarV2CellVariant)
    .nullish(),

  [CbhFeatureFlag.SOLVE_UNPAID_BREAKS_ROLLOUT_STAGE_WITH_TARGETING]:
    solveUnpaidBreaksStageWithTargeting,
  [CbhFeatureFlag.ROLLOUT_SHOW_CORRECT_ANSWERS_ON_QUIZ_SUBMISSION]: z.boolean(),
  [CbhFeatureFlag.ROLLOUT_QUIZ_WORKER_ON_NEGOTIATION]: z.boolean(),
  [CbhFeatureFlag.ENABLE_LUNCH_BREAK_INFO_POPUP]: z.boolean(),
  [CbhFeatureFlag.ROLLOUT_SHIFT_BLOCK_BOOKING]: z.object({
    isBlockBookingEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.ACTIVATION_REFERRAL_SHARE_MESSAGE_CONFIG]: z.object({
    messageTemplate: z.string(),
  }),
  [CbhFeatureFlag.HOME_HEALTH_BOOK_DIALOG_ATTENDANCE_ENABLED]: z.boolean(),
  [CbhFeatureFlag.PERSONAL_ID_SUBTYPES]: z.object({
    manual: z.array(z.string()),
    stripe: z.array(z.string()),
  }),
  [CbhFeatureFlag.SHOW_DEBUG_PAGE_MENU_ITEM_IN_MY_ACCOUNT]: z.boolean(),
  [CbhFeatureFlag.UNDO_BOOKING_TIME_THRESHOLD]: z.number(),
  [CbhFeatureFlag.UNDO_BOOKING_BUFFER_AFTER_BUTTON_CLICK_IN_SECONDS]: z.number(),
  [CbhFeatureFlag.UNDO_BOOKING_LEAD_TIME_CONFIGURATION]: z.object({
    enabled: z.boolean(),
    leadTimeInMinutes: z.number(),
  }),
  [CbhFeatureFlag.CHECK_ALWAYS_ALLOW_LOCATION_SETTING]: z.boolean(),
  [CbhFeatureFlag.ENABLE_WORKER_AVAILABILITY]: z.object({
    qualifications: z.array(z.string()),
  }),
  [CbhFeatureFlag.HOME_HEALTH_VISIT_REQUIREMENT_CHECK]: z.boolean(),
  [CbhFeatureFlag.HYPERTRACK_CLOCK_IN_OUT]: z.boolean(),
  [CbhFeatureFlag.TWILIO_VERIFICATION_API]: z.boolean(),
  [CbhFeatureFlag.SHOW_DEBIT_CARD_EXPIRATION_BANNER]: z.boolean(),
  [CbhFeatureFlag.NEW_CLOCK_OUT_MODAL]: z.boolean(),
  [CbhFeatureFlag.BIOMETRIC_QUALITY_SIGNAL_EXPERIMENT]: z.boolean(),
  [CbhFeatureFlag.PEDOMETER_HISTOGRAM_CONFIG]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(false),
    }),
    z.object({
      enabled: z.literal(true),
      bucketSizeInMinutes: z.number().default(10),
    }),
  ]),
  [CbhFeatureFlag.GET_INSTANT_BOOK_SHIFTS_ENABLED]: z.boolean(),
  [CbhFeatureFlag.NURSE_SHIFT_DOCUMENT_CHECK_FIX]: z.boolean(),
  [CbhFeatureFlag.ROLLOUT_SKILLS_ASSESSMENT_DURING_ONBOARDING]: z.boolean(),
  [CbhFeatureFlag.SKILLS_ASSESSMENTS_CONFIG]: z.object({
    cooldownTimeAfterFailureInDays: z.number(),
    availableQualificationNames: z.array(z.string()),
    maxNumberOfVerifiedShiftsAllowedToBeEligible: z.number(),
    passPercentage: z.number(),
    supportArticleLink: z.string(),
  }),
  [CbhFeatureFlag.SCHEDULED_ASSESSMENTS_CONFIG]: z.object({
    allowedEarlinessInMinutes: z.number(),
    allowedLatenessInMinutes: z.number(),
    hoursUntilAutomaticalFailForBeingLate: z.number(),
    reminderSchedule: z.array(z.number()),
    minimumLeadTimeToScheduleAssessmentInHours: z.number(),
    maximumLeadTimeToScheduleAssessmentInHours: z.number(),
    rescheduleLeadTimeInMinutes: z.number(),
    allowedTimeRange: z
      .object({
        start: z.object({
          hour: z.number(),
          minute: z.number(),
        }),
        end: z.object({
          hour: z.number(),
          minute: z.number(),
        }),
      })
      .optional(),
  }),
  [CbhFeatureFlag.ROLLOUT_SCHEDULED_SKILL_ASSESSMENT]: z.object({
    isEnabled: z.boolean(),
    isClipboardOrientationEnabled: z.boolean(),
    qualificationPriority: z.array(z.string()),
  }),
  [CbhFeatureFlag.IN_APP_RATING_PROMPT]: inAppRatingConfigurationSchema,
  [CbhFeatureFlag.ATTENDANCE_SCORE_INFO]: z.object({
    attendanceScoreInfo: z.boolean(),
    lowAttendanceScoreBanner: z.boolean(),
    disableSecondShiftBonus: z.boolean(),
  }),
  [CbhFeatureFlag.CAREER_AGENT_CONFIG]: z.object({
    showBanner: z.boolean(),
    title: z.string(),
    description: z.string(),
    screenName: z.string(),
    url: z.string(),
    method: z.enum(["iframe", "in-app-browser", "native-browser"]),
  }),
  [CbhFeatureFlag.HELLOSIGN_INSTRUCTIONS_TEXT_ENABLED]: z.boolean(),
  [CbhFeatureFlag.HYPER_TRACK_GEOTAG_EVENTS]: z.object({
    events: z.enum(["none", "failures", "all"]),
  }),
  [CbhFeatureFlag.CLIPBOARD_SCORE_PRIORITY_ACCESS_SHIFTS_PER_WEEK]: z.number(),
  [CbhFeatureFlag.RATE_NEGOTIATION_ACTIVATION]: z.object({
    id: z.array(z.string()),
    state: z.array(z.string()),
    msa: z.array(z.string()),
    global: z.boolean(),
  }),
  [CbhFeatureFlag.RATE_NEGOTIATION_LEAD_TIME]: z.number(),
  [CbhFeatureFlag.ROLLOUT_FEEDBACK_QUALITY_BONUS]: z.object({
    enabled: z.boolean(),
    delayInMinutes: z.number().optional(),
  }),
  [CbhFeatureFlag.PLACEMENT_CANDIDATE_RESUME_PARSING]: z.boolean(),
  [CbhFeatureFlag.FORCE_UPDATE]: z.boolean(),
  [CbhFeatureFlag.APP_UPDATE_NUDGE]: z.boolean(),
  [CbhFeatureFlag.CLIPBOARD_SCORE_LAUNCH_DATE]: z.string().datetime(),
  [CbhFeatureFlag.HIDE_HOUR_RESTRICTION_WARNING_IF_NO_CONFLICTS]: z.boolean(),
  [CbhFeatureFlag.ROLLOUT_JOB_INTERVIEWS]: z.boolean(),
  [CbhFeatureFlag.OS_LEVEL_PUSH_NOTIFICATIONS_SETTINGS]: z.boolean(),
  [CbhFeatureFlag.ACCOUNT_REVIEW_GUIDELINES]: z.string(),
  [CbhFeatureFlag.PLACEMENT_WORKER_TRIALS]: z.boolean(),
  [CbhFeatureFlag.ENABLE_SUPPORT_PAIRING]: z.boolean(),
  [CbhFeatureFlag.WORK_WITH_FRIENDS_V2]: z.boolean(),
  [CbhFeatureFlag.MISSED_SHIFTS_CONFIGURATION]: z.object({
    enabled: z.boolean().default(false),
    maxResultsToShow: z.number().optional(),
    minimumPayPercentile: z.number().optional(),
  }),
  [CbhFeatureFlag.DISCARD_LATENESS_BOOKTIME_THRESHOLD]: z.number(),
  [CbhFeatureFlag.BFF_CLOCK_IN_KILL_SWITCH]: z.boolean(),
  [CbhFeatureFlag.STRIPE_RETURN_LINK_FORCE_REDIRECT]: z.boolean(),
  [CbhFeatureFlag.GEOLOCATION_PREFETCH_FOR_BOOKING]: z.discriminatedUnion("enabled", [
    z.object({ enabled: z.literal(false) }),
    z.object({ enabled: z.literal(true), staleTimeInMinutes: z.number() }),
  ]),
  [CbhFeatureFlag.HIDE_REVIEWS_UI_BY_FACILITY_ID]: z.object({
    facilityIds: z.array(z.string()),
  }),
  [CbhFeatureFlag.HYPERTRACK_ALLOW_MOCK_LOCATION]: z.boolean(),
  [CbhFeatureFlag.WORKER_BADGES]: z.boolean(),
  [CbhFeatureFlag.UNIFY_LATE_CANCELLATION_POLICY_AND_PAYOUT]: z.boolean(),
  [CbhFeatureFlag.PAY_PREFERENCE_FILTER_SETTINGS]: z.object({
    enabled: z.boolean(),
    qualificationRanges: z.record(
      z.string(),
      z.object({
        minimumRate: z.number(),
        maximumRate: z.number(),
      })
    ),
  }),
  [CbhFeatureFlag.SHIFT_SLOTS_V2_PAGE_SIZE]: z.number(),
  [CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING]: z.boolean(),
  [CbhFeatureFlag.GROWTH_WORKER_APP_DEBUGGING_REMOUNT_APP]: z.boolean(),
  [CbhFeatureFlag.ZENDESK_HC_ARTICLE_SSO]: z.boolean(),
  [CbhFeatureFlag.DYNAMIC_PENALTIES_CONFIG]: DynamicPenaltiesConfigSchema,
  [CbhFeatureFlag.INCLUDE_WINDOWED_OUT_SHIFTS]: z.boolean(),
  [CbhFeatureFlag.ENABLE_QR_CODE_DEEP_LINK]: z.boolean(),
  [CbhFeatureFlag.REACTIVATION_BONUS_BANNER]: z.boolean(),
  [CbhFeatureFlag.EDUCATE_FOR_FIRST_N_BOOKINGS]: z.number(),
  [CbhFeatureFlag.BASKET_SIZE_LIMIT_CONFIGURATION]: z.object({
    limited: limitedBasketSizeConfigurationSchema,
    warning: warningBasketSizeConfigurationSchema,
  }),
  [CbhFeatureFlag.SEE_PAST_SHIFTS_AND_PAPER_TIMESHEET]: z.boolean(),
  [CbhFeatureFlag.IN_APP_ADS]: z.object({
    enabled: z.boolean().optional(),
    ads: z
      .array(
        z.object({
          attributes: InAppAdAttributesSchema,
          injectorOptions: InAppAdInjectorOptionsSchema,
        })
      )
      .optional(),
  }),
  [CbhFeatureFlag.DISPLAY_INCIDENT_BANNER]: z.object({
    message: z.string().optional(),
    title: z.string().optional(),
    showCloseButton: z.boolean().optional(),
  }),
  [CbhFeatureFlag.HCP_APP_ERROR_MESSAGES]: z.record(z.string()),
  [CbhFeatureFlag.HIRING_QUALIFICATION_RANKING]: z.array(z.string()),
  [CbhFeatureFlag.DEEP_LINKS_VIA_BACKEND_MAIN]: z.object({
    workerApp: z.boolean().optional(),
  }),
  [CbhFeatureFlag.DELAYED_WORKPLACE_REVIEW]: z.object({
    enabled: z.boolean(),
    delayMinutes: z.number(),
    useDelayedReviewUI: z.boolean().optional(),
  }),
  [CbhFeatureFlag.CLOCK_IN_RULES_REMINDER_ENABLED]: z.object({ isEnabled: z.boolean() }),
  [CbhFeatureFlag.NEWS_FEED_ENABLED]: z.boolean(),
  [CbhFeatureFlag.ENABLE_JOBS_MAP_VIEW_REDIRECTION]: z.boolean(),
  [CbhFeatureFlag.TRAVEL_PAGE_ROLLOUT]: z.object({
    enabled: z.boolean(),
    maxDistanceMiles: z.number(),
    pageSize: z.number(),
    travelPageUrl: z.string(),
    iconName: z.string().optional(),
  }),
  [CbhFeatureFlag.MAX_REFERRAL_BONUS_CONFIG]: z.object({
    onboardingBonusInDollars: z.number(),
    postOnboardingBonusInDollars: z.number(),
  }),
  [CbhFeatureFlag.CANCELLATION_STATS]: z.boolean(),
  [CbhFeatureFlag.ENABLE_FAVORITE_WORKPLACES]: z.object({
    enabled: z.boolean(),
    maxFavoriteCount: z.number(),
    clipboardScoreThreshold: z.number(),
    shiftDiscoveryWindowInDays: z.number(),
  }),
  [CbhFeatureFlag.SHIFT_BOOST_CONFIG]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(true),
      amount: z.number().min(0),
      lifetimeInHours: z.number().int().min(0),
      expirationNoticeInMinutes: z.number().int().min(0),
    }),
    z.object({ enabled: z.literal(false) }),
  ]),
  [CbhFeatureFlag.HACKATHON_TIMECLOCK_DIRECTIONS]: z.boolean(),
  [CbhFeatureFlag.TRACK_INDEXEDDB_SPLASH_HANG]: z.object({
    indexedDbOpenExperiment: z.boolean(),
    appLoadingPageLog: z.object({
      enabled: z.boolean(),
      timeoutInMs: z.number(),
    }),
  }),
  [CbhFeatureFlag.HACK_WEEK_SHIFT_RECOMMENDATIONS]: z.boolean(),
  [CbhFeatureFlag.USE_HELLOSIGN_FALLBACK_URL]: z.boolean(),
  [CbhFeatureFlag.HCP_LICENSE_FIELDS_REQUIREMENTS]: z.record(
    z.string(),
    z.object({
      "license-number": hcpLicenseFieldRequirementSchema,
      state: hcpLicenseFieldRequirementSchema,
      expiry: hcpLicenseFieldRequirementSchema,
      "multi-state": hcpLicenseFieldRequirementSchema,
    })
  ),
  [CbhFeatureFlag.HCP_LICENSE_NUMBER_REQUIREMENTS]: z.record(z.string(), z.array(z.string())),
  [CbhFeatureFlag.NLC_STATES]: z.object({
    active_states: z.array(z.string()),
  }),
  [CbhFeatureFlag.CLIPBOARD_SCORE_REDESIGN]: z.discriminatedUnion("enabled", [
    z.object({ enabled: z.literal(false) }),
    z.object({
      enabled: z.literal(true),
      shiftDiscoveryBanner: z.boolean(),
      page: z.boolean(),
      cancellation: z.boolean(),
      educationFlow: z.boolean().optional(),
      scoreLogs: z.boolean().optional(),
    }),
  ]),
  [CbhFeatureFlag.SHIFT_DISCOVERY_DETECT_STAGE_CHANGES]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.DOCUMENT_EXCLUSION_LIST]: z.object({
    ids: z.array(z.string()).optional(),
  }),
  [CbhFeatureFlag.SHIFT_TRANSFER_ENABLED]: z.boolean(),
  [CbhFeatureFlag.QUALIFICATION_CATEGORIES_CONFIG]: qualificationCategoriesConfigSchema,
  [CbhFeatureFlag.WORKPLACE_REFERRALS]: z.object({
    workerToWorkplace: z.boolean().optional(),
    workerToWorkplaceIncentive: z.record(z.string(), z.number()).optional(),
    workplaceToWorkplace: z.boolean().optional(),
  }),
  [CbhFeatureFlag.WORKER_TO_WORKER_REFERRAL_TYPE]: z.object({
    type: z.enum(["FLEX_ACTIVATION", "FLEX_ACTIVATION_OR_HIRING"]),
  }),
  [CbhFeatureFlag.SHIFT_DISCOVERY_FILTERS_REDESIGN]: z.boolean(),
  [CbhFeatureFlag.PLACEMENTS_QUALIFICATION_FILTER]: z.array(z.string()),
  [CbhFeatureFlag.CONFIG_DOCUMENT_UPLOAD_MULTI_SELECT]: z.object({
    mergePdf: z.boolean(),
    multiFile: z.boolean(),
    multiPhoto: z.boolean(),
  }),
  [CbhFeatureFlag.HIRING_INCENTIVE_PROGRAM]: hiringIncentiveProgramSchema,
  [CbhFeatureFlag.PLACEMENT_RECRUITER_SCREENING]: z.object({
    enabled: z.boolean(),
    showScreeningCard: z.boolean(),
    showCallMeNow: z.boolean(),
    screeningCalendlyLink: z.string(),
    upskillingCalendlyLink: z.string(),
    legacyDocumentRequirementId: z.string(),
  }),
  [CbhFeatureFlag.ARRIVAL_TIME_CONFIGURATION]: z.object({
    gracePeriodInMinutesForDriveETA: z.number(),
    preferredEtaProvider: z.enum(["GOOGLE_MAPS", "AWS_LOCATION", "SHIFT_START"]),
    shortLeadTimeOffsetInHours: z.number(),
    useExpectedArrivalTime: z.boolean(),
  }),
  [CbhFeatureFlag.INDEPENDENT_CONTRACTOR_BADGE_REVAMP]: z.object({
    isEnabled: z.boolean(),
  }),
  [CbhFeatureFlag.CUSTOM_QUALIFICATIONS]: z.boolean(),
  [CbhFeatureFlag.RECRUITER_SCREENING_CALL_NOW_HOURS]: z.object({
    start: z.number(),
    end: z.number(),
    daysOfWeek: z.array(z.nativeEnum(DayOfTheWeek)),
  }),
  [CbhFeatureFlag.MAXIMIZE_BRIGHTNESS_ON_TOUCHPOINT_QR_DISPLAY_STEP]: z.boolean(),
  [CbhFeatureFlag.ENFORCE_NFC_TIMESHEET_BOUNDS]: z.boolean(),
  [CbhFeatureFlag.TIMELINESS_BONUS_REDESIGN_CONFIG]: timelinessBonusRedesignSchema,
  [CbhFeatureFlag.SURFACE_JOBS]: z.boolean(),
  [CbhFeatureFlag.SHIFT_DISCOVERY_DAYS_TO_FETCH]: z.number(),
  [CbhFeatureFlag.CONFIGURE_SSN_REQUIRED_STATES]: z.object({
    enabled: z.boolean(),
    states: z.array(z.string()),
  }),
  [CbhFeatureFlag.CONTRACTOR_AGREEMENT_RE_SIGNING]: z.object({
    enabled: z.boolean(),
    cutoffTimestamp: z.string().datetime(), // ISO date string - the time that the feature flag was rolled out
  }),
  [CbhFeatureFlag.ARRIVAL_TIME_FLOW_WORKPLACE_EXCLUSIONS_CONFIG]: z.object({
    workplaceExclusions: z.object({
      msas: z.array(z.string()),
      workplaceIds: z.array(z.string()),
    }),
  }),
  [CbhFeatureFlag.JOBS_NEW_MARKET_LAUNCH]: z.union([
    z.object({ enabled: z.literal(false).default(false) }),
    z.object({
      enabled: z.literal(true).default(true),
      jobFairMsa: z.string(),
      marketLaunchTimestamp: z.string(),
      jobFairTimestamp: z.string(),
      jobFairHcfId: z.string(),
    }),
  ]),
  [CbhFeatureFlag.ENABLE_NEW_PAYOUT_SETTINGS]: z.boolean(),
  [CbhFeatureFlag.ENABLE_FAILED_PAYOUT_BANNER]: z.boolean(),
  [CbhFeatureFlag.ENABLE_NEW_EARNINGS_PAGE]: z.boolean(),
  [CbhFeatureFlag.ROLLOUT_DETECTED_CHARTING_CREDENTIALS]: z.boolean(),
  [CbhFeatureFlag.FORTUNE_COOKIE_ANIMATION]: z.object({
    enabled: z.boolean(),
    fortuneMessages: z.array(z.string()).default([]),
  }),
  [CbhFeatureFlag.CONFIGURE_LOGIN_SIGNUP_UI]: z.object({
    hideFooterAndSecondaryButtonsOnKeyboardOpen: z.boolean(),
  }),
  [CbhFeatureFlag.ENABLE_CHILDCARE_SUPPORT_CHAT]: z.boolean(),
  [CbhFeatureFlag.ENABLE_VIDEO_POSTING]: z.boolean(),
  [CbhFeatureFlag.HIRING_INSTANT_PAY]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.FORM_1099_UI]: z.discriminatedUnion("enabled", [
    z.object({ enabled: z.literal(false) }),
    z.object({
      enabled: z.literal(true),
      formIssuanceThresholdInDollars: z.number(),
      taxationYear: z.number(),
    }),
  ]),

  [CbhFeatureFlag.RATE_NEGOTIATION_LIMITS]: z.object({
    Maximum: z.string(),
    Minimum: z.string(),
    CNA: z.string().optional(),
    LVN: z.string().optional(),
    RN: z.string().optional(),
  }),
  [CbhFeatureFlag.SHOW_RENEW_LICENSE_INSTRUCTIONS]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.LIVE_ACTIVITIES_CONFIG]: z.object({
    isFeatureEnabled: z.boolean(),
    departureBufferInMinutes: z.number(),
    minimumDistanceMovedForLiveEtaViewInMeters: z.number(),
    minimumEtaDecreaseForLiveEtaViewInMinutes: z.number(),
    liveActivityTriggerLeadTimeMinimumInMinutes: z.number(),
    liveActivityTriggerLeadTimeMaximumInMinutes: z.number(),
    etaFreshnessThresholdInMinutes: z.number(),
    leaveSoonThresholdMinutes: z.number(),
    leaveNowWindowMinutes: z.number(),
    leaveInMinutesWarningThreshold: z.number(),
  }),
  [CbhFeatureFlag.CREDENTIAL_REQUIREMENTS_CONFIGURATION]: z.array(z.string()),
  [CbhFeatureFlag.WORKER_ETA_IMPROVEMENTS]: z.object({
    enabled: z.boolean(),
    isClipboardScorePageChangesEnabled: z.boolean(),
    stationaryMinutesThresholdForEtaNudge: z.number(),
    isLateThresholdMinutes: z.number(),
    isEtaNudgeEnabled: z.boolean(),
    shiftLeadTimeToUpdateEtaMinutes: z.number(),
    maxMinutesAheadToUpdateEta: z.number(),
  }),
  [CbhFeatureFlag.HOLIDAY_GIFT]: z.boolean(),
  [CbhFeatureFlag.SENDBIRD_PAGE_SIZE]: z.discriminatedUnion("enabled", [
    z.object({
      enabled: z.literal(false),
    }),
    z.object({
      enabled: z.literal(true),
      pageSize: z.number().min(0).max(200),
    }),
  ]),
  [CbhFeatureFlag.PLACEMENT_BOOKING_FLOW_ORDER]: z.enum([
    "book_first",
    "screen_first",
    "screen_last",
  ]),
  [CbhFeatureFlag.EMERGENCY_CREDITS_CONFIG]: emergencyCreditsConfigSchema,
  [CbhFeatureFlag.AUTO_CLOCKOUT_SHIFT_END_THRESHOLD]: z.object({
    shiftEndThreshold: z.number().optional(),
    shiftWithGeofenceEndThreshold: z.number().optional(),
  }),
  [CbhFeatureFlag.TIMECLOCK_INFO_WHEN_CONTACTING_SUPPORT]: z.boolean(),
  [CbhFeatureFlag.ENABLE_SUBMIT_HIRING_DOCUMENTS_LATER]: z.boolean(),
  [CbhFeatureFlag.JOB_SEARCH_AGREEMENT_ENABLED]: z.boolean(),
  [CbhFeatureFlag.SIGNUP_JOB_SEARCH_AGREEMENT_CONFIG]: z.nativeEnum(SignupJobSearchAgreementState),
  [CbhFeatureFlag.ROLLOUT_FILTER_BACKGROUND_CHECK_REQUIREMENTS_V2]: z.object({
    enabled: z.boolean(),
    shadow: z.boolean(),
  }),
  [CbhFeatureFlag.RELEASE_WORKER_WORK_BADGES_MAX_UI]: z.object({
    enabled: z.boolean(),
  }),
  [CbhFeatureFlag.INTERACTIVE_SUPPORT_ENABLED]: z.boolean(),
  [CbhFeatureFlag.WORKER_PAY_SURVEY_CONFIG]: z.object({
    variation: z.enum(["on", "off"]),
  }),
  [CbhFeatureFlag.ATTENDANCE_TRACKING_METHODS_ROLLOUT]: z.object({
    state: z.enum(["off", "dry-run", "on"]),
    minimalOtaVersion: z.union([z.string(), z.number()]),
    minimalStoreVersion: z.string(),
    methodsConfiguration: z
      .record(
        z.enum([
          "GEOFENCE",
          "NFC_TAG",
          "UKG_INTEGRATION",
          "ATTENDANCE_ON_DEMAND_INTEGRATION",
          "MESH_INTEGRATION",
          "TAKE_A_VIDEO",
          "AI_TAKE_A_VIDEO",
        ]),
        z.object({
          minimalOtaVersion: z.union([z.string(), z.number()]),
          minimalStoreVersion: z.string(),
          state: z.enum(["enabled", "disabled"]),
        })
      )
      .optional(),
  }),
  [CbhFeatureFlag.ENABLE_PAYMENT_DETAILS_PAGE]: z.boolean(),
  [CbhFeatureFlag.ENABLE_BADGE_VERIFICATION_CLOCK_IN]: z.boolean(),
  [CbhFeatureFlag.WORKER_FORCED_CANCELLATION_CHAT_WARNING]: z.object({
    workplaceIds: z.array(z.string()),
  }),
  [CbhFeatureFlag.ENABLE_CONNECTED_JOB_LISTINGS]: z.object({
    enabled: z.boolean(),
    workplaceTypes: z.array(z.string()).optional(),
  }),
  [CbhFeatureFlag.EARLY_ACCESS_FACILITIES]: z.boolean(),
  [CbhFeatureFlag.CONFIGURE_WORK_BADGE_SETTINGS]: z.object({
    workBadgeVerificationPassedScore: z.number().int().positive(),
    workBadgeVerificationFailedOrSkippedScore: z.number().int().negative(),
  }),
  [CbhFeatureFlag.CHAT_CANCELLATION_DETECTION]: z.boolean(),
  [CbhFeatureFlag.LOCATION_CHANGE_PROMPT_CONFIG]: z.object({
    enabled: z.boolean(),
    cooldownInHours: z.number(),
    distanceThresholdInMiles: z.number(),
  }),
  [CbhFeatureFlag.GUARANTEED_PAY_WAITLIST]: z.boolean(),
  [CbhFeatureFlag.CONCORDE_SCHOLARSHIP]: z.boolean(),
  [CbhFeatureFlag.ALCHEMY_CLIPBOARD_GUIDES_EXPERIMENT]: z.object({
    verifiedShiftsLookBackPeriodInDays: z.number().int().min(1),
    offsetToShiftEndForWorkerRemoval: z.number().int().min(0),
    allowedQualifications: z.array(z.string()),
    channelUrlSuffix: z.string(),
    channelNamePrefix: z.string(),
    experiencedWorkerLimit: z.number().int().min(1),
    enabled: z.boolean(),
    notifyExperiencedWorkers: z.boolean().optional(),
    recentMessageLimit: z.number().int().min(1).max(20).optional(),
    llmClassificationEnabled: z.boolean().optional(),
    llmConfig: z
      .object({
        messages: z.array(
          z.object({
            content: z.string(),
            role: z.enum(["user", "developer"]),
          })
        ),
        provider: z.literal("openai"),
        model: z.string(),
        temperature: z.number().optional(),
      })
      .optional(),
  }),
};

export type CbhFeatureFlags = {
  [K in keyof typeof cbhFeatureFlags]: z.infer<(typeof cbhFeatureFlags)[K]>;
};
